import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import PublicLayout from '../../components/layout/PublicLayout';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { authAPI } from '../../services/api';
import geminiLogo from '../../Gemini_Generated_Image_rvsyaprvsyaprvsy.png';

const Register = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendingOTP, setResendingOTP] = useState(false);
  const [selectedRole, setSelectedRole] = useState('buyer');
  
  const {
    register: registerForm,
    handleSubmit: handleRegisterSubmit,
    formState: { errors: registerErrors },
    watch
  } = useForm();

  const {
    register: registerOTP,
    handleSubmit: handleOTPSubmit,
    formState: { errors: otpErrors },
    setValue: setOTPValue
  } = useForm();

  const password = watch('password');

  const onRegisterSubmit = async (data) => {
    setSubmitting(true);
    
    try {
      // Transform data to match API format from curl request
      const registrationData = {
        username: data.username, // Use separate username field
        email: data.email,
        phoneNumber: data.mobileNumber, // API expects phoneNumber (using mobileNumber)
        password: data.password,
        confirmPassword: data.confirmPassword,
        role: selectedRole.toUpperCase() // Convert to uppercase to match backend enum
      };

      const response = await authAPI.register(registrationData);
      
      // Show OTP verification screen after successful registration
      setRegisteredEmail(data.email);
      setShowOTP(true);
      toast.success('Registration successful! Please verify your email.');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const onOTPSubmit = async (data) => {
    setSubmitting(true);
    
    try {
      const response = await authAPI.verifyOTP(registeredEmail, data.otp,"EMAIL_VERIFICATION",selectedRole?.toUpperCase());
      
      if (response.data.success) {
        toast.success('Email verified successfully! Please login.');
        navigate('/login?role=' + selectedRole);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid OTP';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    setResendingOTP(true);
    
    try {
      await authAPI.forgotPassword(registeredEmail);
      toast.success('OTP resent to your email!');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to resend OTP';
      toast.error(errorMessage);
    } finally {
      setResendingOTP(false);
    }
  };

  const handleBackToRegister = () => {
    setShowOTP(false);
    setRegisteredEmail('');
    setOTPValue('otp', '');
  };

  // Show OTP Verification Screen
  if (showOTP) {
    return (
      <PublicLayout title="Verify Email" showBack>
        <div style={{ maxWidth: '400px', margin: '0 auto' }}>
          <div className="card">
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'var(--primary-light)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Icon name="mail" size={32} color="var(--primary)" />
              </div>
              <h2 style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: '700',
                fontSize: '24px',
                color: 'var(--text)',
                marginBottom: '8px'
              }}>
                Verify Your Email
              </h2>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                We've sent a 6-digit verification code to<br />
                <strong>{registeredEmail}</strong>
              </p>
            </div>

            {/* OTP Form */}
            <form onSubmit={handleOTPSubmit(onOTPSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* 6-Digit OTP Input */}
              <div>
                <label className="label">Verification Code *</label>
                <input
                  {...registerOTP('otp', { 
                    required: 'Verification code is required',
                    pattern: {
                      value: /^[0-9]{6}$/,
                      message: 'OTP must be 6 digits'
                    }
                  })}
                  type="text"
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '24px',
                    letterSpacing: '12px',
                    fontFamily: 'monospace',
                    fontWeight: '600',
                    padding: '16px',
                    borderColor: otpErrors.otp ? 'var(--red)' : 'var(--border)'
                  }}
                />
                {otpErrors.otp && (
                  <p className="text-red-500" style={{ textAlign: 'center', marginTop: '8px' }}>
                    {otpErrors.otp.message}
                  </p>
                )}
              </div>

              {/* Verify Button */}
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={submitting}
                style={{ width: '100%', padding: '12px' }}
              >
                Verify Email
              </Button>

              {/* Resend OTP */}
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                  Didn't receive the code?{' '}
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendingOTP}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: resendingOTP ? 'var(--text-muted)' : 'var(--primary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: resendingOTP ? 'not-allowed' : 'pointer',
                      textDecoration: 'none'
                    }}
                  >
                    {resendingOTP ? 'Sending...' : 'Resend Code'}
                  </button>
                </span>
              </div>

              {/* Back to Register */}
              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={handleBackToRegister}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary)',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  ← Back to Registration
                </button>
              </div>
            </form>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // Show Registration Form
  return (
    <PublicLayout title="Register" showBack>
      <div style={{ maxWidth: '480px', margin: '0 auto' }}>
        <div className="card">
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '8px',
              marginBottom: '16px'
            }}>
              <img 
                src={geminiLogo}
                alt="ZMA Online Logo"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '10px'
                }}
              />
              <span style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: '800',
                fontSize: '20px',
                color: 'var(--text)'
              }}>
                ZMA<span style={{ color: 'var(--primary)' }}>Online</span>
              </span>
            </div>
            <h2 style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: '700',
              fontSize: '24px',
              color: 'var(--text)',
              marginBottom: '8px'
            }}>
              Create {selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)} Account
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
              {selectedRole === 'buyer' && 'Join thousands of customers shopping for deals'}
              {/* {selectedRole === 'seller' && 'Start selling your products on our platform'} */}
              {/* {selectedRole === 'mediator' && 'Join our network of deal mediators'} */}
            </p>
          </div>

          {/* Role Selector */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Register as</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {[
                { key: 'buyer', label: 'Buyer', desc: 'Buy products' },
                // { key: 'seller', label: 'Seller', desc: 'Sell products' },
                // { key: 'mediator', label: 'Mediator', desc: 'Manage deals' }
              ].map((roleOption) => (
                <button
                  key={roleOption.key}
                  type="button"
                  onClick={() => setSelectedRole(roleOption.key)}
                  style={{
                    padding: '12px 8px',
                    borderRadius: '8px',
                    border: `2px solid ${selectedRole === roleOption.key ? 'var(--primary)' : 'var(--border)'}`,
                    background: selectedRole === roleOption.key ? 'var(--primary-light)' : 'var(--bg-deep)',
                    color: selectedRole === roleOption.key ? 'var(--primary-dark)' : 'var(--text-sub)',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '2px' }}>{roleOption.label}</div>
                  <div style={{ fontSize: '11px', opacity: 0.8 }}>{roleOption.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleRegisterSubmit(onRegisterSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Username */}
            <div>
              <label className="label">Username *</label>
              <input
                {...registerForm('username', { 
                  required: 'Username is required',
                  minLength: { value: 3, message: 'Username must be at least 3 characters' },
                  pattern: {
                    value: /^[a-zA-Z0-9_]+$/,
                    message: 'Username can only contain letters, numbers, and underscores'
                  }
                })}
                type="text"
                placeholder="Choose a username"
                style={{
                  width: '100%',
                  borderColor: registerErrors.username ? 'var(--red)' : 'var(--border)'
                }}
              />
              {registerErrors.username && (
                <p className="text-red-500">{registerErrors.username.message}</p>
              )}
            </div>

            {/* Email ID */}
            <div>
              <label className="label">Email ID *</label>
              <input
                {...registerForm('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                type="email"
                placeholder="Enter your email address"
                style={{
                  width: '100%',
                  borderColor: registerErrors.email ? 'var(--red)' : 'var(--border)'
                }}
              />
              {registerErrors.email && (
                <p className="text-red-500">{registerErrors.email.message}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label className="label">Phone Number *</label>
              <div style={{ display: 'flex' }}>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 12px',
                  background: 'var(--bg-deep)',
                  border: '1px solid var(--border)',
                  borderRight: 'none',
                  borderRadius: '10px 0 0 10px',
                  fontSize: '14px',
                  color: 'var(--text-sub)'
                }}>+91</span>
                <input
                  {...registerForm('mobileNumber', {
                    required: 'Phone number is required',
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: 'Invalid phone number'
                    }
                  })}
                  placeholder="9876543210"
                  style={{
                    flex: '1',
                    borderColor: registerErrors.mobileNumber ? 'var(--red)' : 'var(--border)',
                    borderRadius: '0 10px 10px 0'
                  }}
                />
              </div>
              {registerErrors.mobileNumber && (
                <p className="text-red-500">{registerErrors.mobileNumber.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="label">Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  {...registerForm('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  style={{
                    width: '100%',
                    paddingRight: '40px',
                    borderColor: registerErrors.password ? 'var(--red)' : 'var(--border)'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer'
                  }}
                >
                  <Icon name={showPassword ? 'eye-off' : 'eye'} size={16} />
                </button>
              </div>
              {registerErrors.password && (
                <p className="text-red-500">{registerErrors.password.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="label">Confirm Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  {...registerForm('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: value => value === password || 'Passwords do not match'
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  style={{
                    width: '100%',
                    paddingRight: '40px',
                    borderColor: registerErrors.confirmPassword ? 'var(--red)' : 'var(--border)'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer'
                  }}
                >
                  <Icon name={showConfirmPassword ? 'eye-off' : 'eye'} size={16} />
                </button>
              </div>
              {registerErrors.confirmPassword && (
                <p className="text-red-500">{registerErrors.confirmPassword.message}</p>
              )}
            </div>

            {/* Terms & Conditions */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
              <input
                {...registerForm('termsAccepted', {
                  required: 'You must accept the terms and conditions'
                })}
                type="checkbox"
                style={{ 
                  accentColor: 'var(--primary)', 
                  marginTop: '3px', 
                  width: '15px', 
                  height: '15px' 
                }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-sub)' }}>
                I agree to the <a href="#" style={{ color: 'var(--primary)', textDecoration: 'none' }}>Terms & Conditions</a> and <a href="#" style={{ color: 'var(--primary)', textDecoration: 'none' }}>Privacy Policy</a> *
              </span>
            </label>
            {registerErrors.termsAccepted && (
              <p className="text-red-500">{registerErrors.termsAccepted.message}</p>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={submitting}
              style={{ width: '100%', padding: '12px' }}
            >
              Create Account
            </Button>

            {/* Login Link */}
            <div style={{ textAlign: 'center', paddingTop: '8px' }}>
              <span style={{ fontSize: '14px', color: 'var(--text-sub)' }}>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login?role=' + selectedRole)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary)',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                >
                  Sign In
                </button>
              </span>
            </div>
          </form>
        </div>
      </div>
    </PublicLayout>
  );
};

export default Register;
